import React from "react";

/**
 * FighterBody — a full-body meme-coin character.
 * Head = shiny coin (with initial + eyes + angry brows). Body = cartoon torso with
 * emblem shield, boxing-glove arms, sneakered legs, and a wagging tail.
 * Mirrored horizontally when facing left so the two fighters face each other.
 *
 * Props:
 *   fighter   – fighter data object
 *   facing    – "right" (default) or "left"
 *   state     – "idle" | "walk" | "attackPunch" | "attackSpecial" | "hurt" | "heal" | "ko"
 *   size      – pixel height
 */
export const FighterBody = ({ fighter, facing = "right", state = "idle", size = 230 }) => {
  const { color, accent, initial, id } = fighter;

  const wrapperAnim =
    {
      idle: "anim-body-idle",
      walk: "anim-body-walk",
      attackPunch: facing === "right" ? "anim-body-lunge-right" : "anim-body-lunge-left",
      attackSpecial: facing === "right" ? "anim-body-slam-right" : "anim-body-slam-left",
      hurt: facing === "right" ? "anim-body-hurt-right" : "anim-body-hurt-left",
      heal: "anim-body-heal",
      ko: facing === "right" ? "anim-body-ko-right" : "anim-body-ko-left",
    }[state] || "anim-body-idle";

  const armsAnim =
    {
      attackPunch: "anim-arms-punch",
      attackSpecial: "anim-arms-slam",
      walk: "anim-arms-walk",
    }[state] || "";

  const legsAnim = state === "walk" ? "anim-legs-walk" : state === "ko" ? "" : "anim-legs-idle";

  const gradId = `coin-g-${id}`;
  const dotsId = `coin-d-${id}`;

  return (
    <div
      className={`relative ${wrapperAnim}`}
      style={{
        width: size * 0.78,
        height: size,
        transform: facing === "left" ? "scaleX(-1)" : "none",
        transformOrigin: "50% 100%",
      }}
      data-testid={`fighter-body-${id}`}
    >
      {state === "heal" && (
        <div className="absolute inset-0 anim-heal-glow pointer-events-none rounded-full" />
      )}
      <svg viewBox="0 0 200 280" width="100%" height="100%" style={{ overflow: "visible" }}>
        <defs>
          <radialGradient id={gradId} cx="35%" cy="30%" r="80%">
            <stop offset="0%" stopColor="#fff" stopOpacity="0.9" />
            <stop offset="40%" stopColor={color} />
            <stop offset="100%" stopColor={accent} />
          </radialGradient>
          <pattern id={dotsId} x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1" fill="rgba(0,0,0,0.25)" />
          </pattern>
        </defs>

        {/* Floor shadow */}
        <ellipse cx="100" cy="268" rx="60" ry="7" fill="#000" opacity="0.3" />

        {/* --------- LEGS --------- */}
        <g className={legsAnim}>
          <g className="leg-left" style={{ transformOrigin: "82px 215px" }}>
            <rect x="72" y="212" width="22" height="45" rx="6" fill={color} stroke="#000" strokeWidth="4" />
            <ellipse cx="82" cy="262" rx="18" ry="8" fill="#111" stroke="#000" strokeWidth="3" />
            <ellipse cx="82" cy="259" rx="18" ry="4" fill="#fff" opacity="0.4" />
          </g>
          <g className="leg-right" style={{ transformOrigin: "118px 215px" }}>
            <rect x="106" y="212" width="22" height="45" rx="6" fill={color} stroke="#000" strokeWidth="4" />
            <ellipse cx="118" cy="262" rx="18" ry="8" fill="#111" stroke="#000" strokeWidth="3" />
            <ellipse cx="118" cy="259" rx="18" ry="4" fill="#fff" opacity="0.4" />
          </g>
        </g>

        {/* --------- TAIL --------- */}
        <g className="anim-tail-wag" style={{ transformOrigin: "160px 175px" }}>
          <path
            d="M 160 175 Q 190 155 196 125 Q 184 140 170 158 Z"
            fill={color}
            stroke="#000"
            strokeWidth="4"
            strokeLinejoin="round"
          />
        </g>

        {/* --------- TORSO + EMBLEM --------- */}
        <g>
          <path
            d="M 55 155 Q 55 110 100 110 Q 145 110 145 155 L 148 210 Q 148 230 100 230 Q 52 230 52 210 Z"
            fill={`url(#${gradId})`}
            stroke="#000"
            strokeWidth="4"
            strokeLinejoin="round"
          />
          <path
            d="M 55 155 Q 55 110 100 110 Q 145 110 145 155 L 148 210 Q 148 230 100 230 Q 52 230 52 210 Z"
            fill={`url(#${dotsId})`}
            opacity="0.5"
          />
          <g>
            <path d="M 78 155 L 122 155 L 128 180 L 100 202 L 72 180 Z" fill="#000" />
            <path d="M 82 158 L 118 158 L 123 178 L 100 196 L 77 178 Z" fill={accent} />
            <text
              x="100"
              y="183"
              textAnchor="middle"
              fontFamily="Bungee, Impact, sans-serif"
              fontSize="14"
              fill="#fff"
              transform={facing === "left" ? "scale(-1 1) translate(-200 0)" : undefined}
            >
              {fighter.ticker.replace("$", "")}
            </text>
          </g>
        </g>

        {/* --------- ARMS --------- */}
        <g className={armsAnim}>
          <g className="arm-back" style={{ transformOrigin: "60px 135px" }}>
            <path d="M 55 135 Q 25 150 20 190" stroke={color} strokeWidth="22" fill="none" strokeLinecap="round" />
            <path d="M 55 135 Q 25 150 20 190" stroke="#000" strokeWidth="4" fill="none" strokeLinecap="round" />
            <circle cx="20" cy="195" r="18" fill="#DD2E44" stroke="#000" strokeWidth="4" />
            <circle cx="15" cy="190" r="6" fill="#fff" opacity="0.7" />
          </g>
          <g className="arm-front" style={{ transformOrigin: "140px 135px" }}>
            <path d="M 145 135 Q 180 145 190 185" stroke={color} strokeWidth="22" fill="none" strokeLinecap="round" />
            <path d="M 145 135 Q 180 145 190 185" stroke="#000" strokeWidth="4" fill="none" strokeLinecap="round" />
            <circle cx="190" cy="190" r="20" fill="#DD2E44" stroke="#000" strokeWidth="4" />
            <circle cx="185" cy="183" r="7" fill="#fff" opacity="0.7" />
          </g>
        </g>

        {/* --------- HEAD (COIN) --------- */}
        <g className="anim-head-bob" style={{ transformOrigin: "100px 60px" }}>
          <rect x="90" y="100" width="20" height="18" fill={accent} stroke="#000" strokeWidth="3" />
          <circle cx="100" cy="58" r="52" fill="#000" />
          <circle cx="100" cy="58" r="46" fill={`url(#${gradId})`} />
          <circle cx="100" cy="58" r="40" fill="none" stroke={accent} strokeWidth="3" strokeDasharray="5 3" />
          <ellipse cx="82" cy="38" rx="18" ry="8" fill="#fff" opacity="0.65" />
          <text
            x="100"
            y="73"
            textAnchor="middle"
            fontFamily="Bungee, Impact, sans-serif"
            fontSize="46"
            fill="#000"
            transform={facing === "left" ? "scale(-1 1) translate(-200 0)" : undefined}
          >
            {initial}
          </text>
          {/* Eyes */}
          <circle cx="78" cy="48" r="6" fill="#000" />
          <circle cx="76" cy="46" r="2" fill="#fff" />
          <circle cx="122" cy="48" r="6" fill="#000" />
          <circle cx="120" cy="46" r="2" fill="#fff" />
          {/* Angry brows */}
          <path d="M 68 36 L 86 42" stroke="#000" strokeWidth="4" strokeLinecap="round" />
          <path d="M 132 36 L 114 42" stroke="#000" strokeWidth="4" strokeLinecap="round" />
        </g>
      </svg>
    </div>
  );
};

export default FighterBody;
